import RootLayout from '@c/layouts/RootLayout';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getHostUrl } from '@util/index';
import { generateRandomId, isAuthTokenLive, logError } from '@util/logError';
import { captureAttribution } from '@util/marketing';
import AuthProvider from 'context/AuthContext';
import { ChatProvider } from 'context/ChatContext';
import { ShoppingCartProvider } from 'context/ShoppingCartContext';
import { StoreSaleProvider, type PublicStoreSale } from 'context/StoreSaleContext';
import { StripeProvider } from 'context/StripeContext';
import { ToastProvider } from 'context/ToastContext';
import { NextPage } from 'next';
import type { AppProps, AppType } from 'next/app';
import Head from 'next/head';
import { useRouter as useNavigation } from 'next/navigation';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Cookie from 'js-cookie';
import 'react-toastify/dist/ReactToastify.css';
// Design-system tokens (gearfocus-ui). Imported BEFORE globals.css so globals can
// override --font-sans — tokens.scss declares Inter, production uses General Sans.
import 'ui/tokens/tokens.scss';
import 'styles/globals.css';
import AppErrorBoundary from './AppErrorBoundary';
import { HeadMetaTag } from './blog/[...slug]';
import ConsentBanner from '@c/termly/ConsentBanner';

// Define custom types
type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

// Create a QueryClient instance with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

// A component to provide context providers and error handling
function ProviderProvider({ children, initialStoreSales = [], initialStoreSalesNowMs }: {
  children: ReactNode;
  initialStoreSales?: PublicStoreSale[];
  initialStoreSalesNowMs?: number;
}) {
  const router = useNavigation();
  const id = useMemo(() => generateRandomId(), []);

  return (
    <ErrorBoundary
      onError={(error, { componentStack }) => {
        // Read route/auth directly off window/cookie rather than through
        // React context — if AuthProvider itself is what crashed, its
        // context is unavailable here (this boundary sits above it).
        const route =
          typeof window !== 'undefined' ? window.location.pathname : undefined;
        const authCookie = Cookie.get('auth');
        const authState =
          authCookie && isAuthTokenLive(authCookie) ? 'authenticated' : 'guest';
        logError(error, id, componentStack, { route, authState });
      }}
      fallbackRender={({ error }: { error: Error }) => {
        const msg = error instanceof Error ? error.message : error;
        return <AppErrorBoundary message={msg} id={id} />;
      }}
      onReset={() => router.refresh()}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <StripeProvider>
            <ToastProvider>
              <StoreSaleProvider initialSales={initialStoreSales} initialNowMs={initialStoreSalesNowMs}>
                <ShoppingCartProvider>
                  <ChatProvider>{children}</ChatProvider>
                  {/*
                    Termly cookie consent only loads in production. In dev /
                    preview its third-party axios calls to app.termly.io can
                    fail silently (ad blockers, transient outages), which
                    surfaces as an "Unhandled Runtime Error" in the Next.js
                    overlay and blocks testing. The banner is only legally
                    required on the live site anyway.
                  */}
                  {process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' && (
                    <ConsentBanner websiteUUID="284e71c1-d4f7-4908-8af1-53ec89a77b0e" />
                  )}
                </ShoppingCartProvider>
              </StoreSaleProvider>
            </ToastProvider>
          </StripeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
// Define the main App component
const App: AppType = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? ((page) => page);
  const router = useRouter();
  const layout = getLayout(<Component {...pageProps} />);
  const showDots = pageProps.title && pageProps.title.length > 87;

  // Capture marketing attribution (UTMs, referrer, landing page) on first visit
  // Only runs once per session - data is stored in a 30-day cookie
  useEffect(() => {
    try {
      captureAttribution();
    } catch (e) {
      // Silently fail - attribution is not critical to site function
      console.error('[Attribution] Capture failed:', e);
    }
  }, []);

  // Default title used for pages that don't use the SEO component
  const defaultTitle = router.asPath.includes('/dashboard')
    ? 'Dashboard | GearFocus'
    : 'GearFocus - Buy and Sell Camera Gear';

  // Set canonical URL for SEO
  const canonicalUrl = router.query.tab
    ? `https://gearfocus.com/shop-seller/gerald-undone/${router.query.tab}`
    : `https://gearfocus.com${router.asPath.split('?')[0]}`;

  return (
    <RootLayout>
      {/* Only render these Head tags when pageProps.metaTags exists (for pages not using SEO component) */}
      {pageProps.metaTags && (
        <Head>
          <title>
            {pageProps.title
              ? `${pageProps.title?.slice(0, 90)}${showDots ? '...' : ''}`
              : defaultTitle}
          </title>

          {/* Canonical URL */}
          <link rel="canonical" href={canonicalUrl} />

          {pageProps.metaTags?.map((tag: HeadMetaTag, i: number) => (
            <meta
              key={tag.key || i}
              property={tag.property}
              content={tag.content}
              {...tag.attributes}
            />
          ))}

          <meta
            key="twitter:card"
            name="twitter:card"
            content="summary_large_image"
          />
          <meta
            key="og:site_name"
            property="og:site_name"
            content="GearFocus"
          />
          <meta
            key="og:url"
            property="og:url"
            content={getHostUrl() + router.asPath}
          />
          <meta name="author" content="GearFocus" />
        </Head>
      )}

      {/* Always include these Head elements */}
      <Head>
        <meta charSet="UTF-8" />
        {/*
          NOTE (PR #1801, GB #1802): https://maps.googleapis.com appears in both script-src and
          connect-src below because Stripe's Address Element injects the Google Maps Places script
          into this document itself (via the js.stripe.com/v3 loader) — there is no global Maps
          <script> tag anywhere in this app. Do not remove either entry as "unused"; that will
          silently break address autocomplete on every non-checkout address surface (seller
          registration, add-address modal, create listing, shop settings).
        */}
        <meta
          httpEquiv="Content-Security-Policy"
          content="
            default-src *;
            style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://static.klaviyo.com;
            style-src-elem 'self' 'unsafe-inline' 
              https://fonts.googleapis.com 
              https://static.klaviyo.com
              https://static-tracking.klaviyo.com;
            script-src 'self' 'unsafe-inline' 'unsafe-eval' 'unsafe-hashes'
              http://www.google.com
              https://js.stripe.com
              https://r.stripe.com
              https://www.googletagmanager.com
              https://cdnjs.cloudflare.com
              https://connect.facebook.net
              https://maps.googleapis.com
              https://apis.google.com
              https://pagead2.googlesyndication.com
              https://tpc.googlesyndication.com
              https://googleads.g.doubleclick.net
              https://vercel.live
              https://www.gstatic.com
              https://static.hotjar.com
              https://script.hotjar.com
              https://scripts.clarity.ms
              https://clarity.ms
              https://www.clarity.ms
              https://s.pinimg.com
              https://ct.pinterest.com
              https://us-central1-gear-focus-staging.cloudfunctions.net
              https://us-central1-gear-focus.cloudfunctions.net
              https://firestore.googleapis.com
              https://*.firebaseio.com
              https://ss.gearfocus.com
              https://ss3.zone.gearfocus.com
              https://server.gearfocus.com
              https://warm.gearfocus.com
              https://rr.revenueroll.com
              https://stapecdn.com/dtag/v8.js
              https://tracker.metricool.com/resources/be.js
              https://static.klaviyo.com
              https://static-tracking.klaviyo.com
              https://www.googleadservices.com
              https://rr.revenueroll.com
              https://bat.bing.com
              https://app.termly.io
              https://ep2.adtrafficquality.google
              https://cdn.jsdelivr.net
              https://omnisnippet1.com;
            connect-src 'self' *
              https://maps.googleapis.com
              https://b.api.refocus.store
              https://ct.pinterest.com
              https://content.hotjar.io
              https://clarity.ms
              https://www.clarity.ms
              https://scripts.clarity.ms
              https://static.hotjar.com
              https://script.hotjar.com
              https://wt.omnisendlink.com
              https://apis.google.com
              https://firestore.googleapis.com
              https://identitytoolkit.googleapis.com
              https://securetoken.googleapis.com
              https://*.firebaseio.com
              https://firebasestorage.googleapis.com
              https://us-central1-gear-focus-staging.cloudfunctions.net
              https://us-central1-gear-focus.cloudfunctions.net
              https://www.google-analytics.com
              https://analytics.google.com
              https://stats.g.doubleclick.net
              https://www.googletagmanager.com
              https://bat.bing.com
              https://connect.facebook.net
              https://r.stripe.com
              wss://*.firebaseio.com
              https://ss3.zone.gearfocus.com;
            font-src * data:
              https://fonts.googleapis.com
              https://fonts.gstatic.com
              https://js.stripe.com;
            img-src * blob: data:;
            frame-src 'self'
              https://www.youtube.com
              https://www.google.com
              https://js.stripe.com
              https://m.stripe.network
              https://ct.pinterest.com
              https://www.facebook.com
              https://td.doubleclick.net
              https://www.googletagmanager.com
              https://ss.gearfocus.com
              https://server.gearfocus.com
              https://gear-focus.firebaseapp.com
              https://gear-focus-staging.firebaseapp.com
              https://*.firebaseio.com
              https://googleads.g.doubleclick.net
              https://manage.realtourvision.com
              https://vercel.live
              https://ss3.zone.gearfocus.com;
          "
        />

        <meta name="google-adsense-account" content="ca-pub-5059820352612148" />
        <link rel="dns-prefetch" href="https://google.com" />
        <link rel="preconnect" href="https://gearfocus.b-cdn.net" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://gearfocus.b-cdn.net" />
      </Head>

      {/* APPLICATION CONTENT */}
      <ProviderProvider initialStoreSales={pageProps.initialStoreSales}
        initialStoreSalesNowMs={pageProps.initialStoreSalesNowMs}>{layout}</ProviderProvider>

      {/* UTILITY SCRIPTS */}
      <Script
        src="https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.3.2/lazysizes.min.js"
        strategy="lazyOnload"
        async
      />
    </RootLayout>
  );
};

export default App;
